<template>
  <b-modal
    v-model="visible"
    centered
    no-close-on-backdrop
    hide-header
    hide-footer
    size="lg"
    @hide="onHide"
  >
    <div class="atm-root">
      <button class="atm-close" @click="onHide" aria-label="Close">
        <b-icon icon="x" />
      </button>

      <!-- Header -->
      <div class="atm-header">
        <div class="atm-header__icon-wrap">
          <b-icon icon="plus-circle" class="atm-header__icon" />
        </div>
        <div>
          <p class="atm-header__title">Add TPA Billing</p>
          <p class="atm-header__sub" v-if="underwriterName">
            For <strong>{{ underwriterName }}</strong>
          </p>
        </div>
      </div>

      <!-- Step 1 — Select TPAs -->
      <div v-if="step === 1">
        <p class="atm-section-label">Select TPA Companies</p>
        <p v-if="!optionsScoped" class="text-warning small mb-2">
          Couldn't check which TPAs share a LimeIQ Key with this underwriter, so every TPA is listed.
        </p>

        <!-- Search -->
        <div class="atm-search mb-3">
          <b-input-group class="input-group-alternative input-group-merge">
            <div class="input-group-prepend">
              <span class="input-group-text"><i class="fas fa-search"></i></span>
            </div>
            <b-form-input
              v-model="tpaSearch"
              placeholder="Search TPA..."
              size="sm"
              type="text"
            />
          </b-input-group>
        </div>

        <!-- TPA list -->
        <div class="atm-tpa-list">
          <div v-if="filteredTpas.length === 0" class="text-center text-muted py-4 small">
            <template v-if="availableTpas.length === 0 && optionsScoped">
              No TPAs share a LimeIQ Key led by this underwriter.
            </template>
            <template v-else>No TPAs available to add.</template>
          </div>
          <div
            v-for="tpa in filteredTpas"
            :key="tpa.id"
            class="atm-tpa-item"
            :class="{ 'atm-tpa-item--selected': isSelected(tpa.id) }"
            @click="toggleTpa(tpa.id)"
          >
            <div class="atm-tpa-item__check">
              <b-icon :icon="isSelected(tpa.id) ? 'check-square-fill' : 'square'" />
            </div>
            <span class="atm-tpa-item__name">{{ tpa.name }}</span>
          </div>
        </div>

        <div class="atm-footer">
          <span class="text-muted small">{{ selectedIds.length }} selected</span>
          <div class="d-flex gap-2">
            <b-button variant="secondary" @click="onHide">Cancel</b-button>
            <b-button
              variant="primary"
              :disabled="selectedIds.length === 0"
              @click="step = 2"
            >
              Next: Set Date
            </b-button>
          </div>
        </div>
      </div>

      <!-- Step 2 — Pick billing start date -->
      <div v-if="step === 2">
        <p class="atm-section-label">Set Billing Start Date</p>

        <div class="atm-selected-summary mb-3">
          <b-icon icon="check-circle-fill" class="text-success mr-1" />
          <span class="small">
            <strong>{{ selectedIds.length }}</strong> TPA{{ selectedIds.length !== 1 ? 's' : '' }} selected
          </span>
          <a href class="small ml-2 text-info" @click.prevent="step = 1">Change</a>
        </div>

        <!-- Selected TPA chips -->
        <div class="atm-chips mb-3">
          <span
            v-for="id in selectedIds"
            :key="id"
            class="atm-chip"
          >{{ getTpaName(id) }}</span>
        </div>

        <div class="atm-date-wrap">
          <label class="atm-date-label">Billing Start Date</label>
          <DatePicker
            v-model="billingStartPeriod"
            type="date"
            format="DD MMM YYYY"
            value-type="YYYY-MM-DD"
            placeholder="Select start date"
            :disabled-date="disabledBeforeToday"
            :clearable="false"
            class="atm-date-picker"
          />
          <p class="atm-date-hint">Optional — leave blank to add the TPA with billing inactive.</p>
        </div>

        <b-alert v-if="errorMsg" show variant="danger" class="mt-3 small py-2">
          {{ errorMsg }}
        </b-alert>

        <div class="atm-footer">
          <b-button variant="link" class="text-muted px-0" @click="step = 1">Back</b-button>
          <div class="d-flex gap-2">
            <b-button variant="secondary" @click="onHide">Cancel</b-button>
            <b-button
              variant="primary"
              :disabled="saving"
              @click="confirmAdd"
            >
              {{ saving ? 'Adding…' : 'Add TPA Billing' }}
            </b-button>
          </div>
        </div>
      </div>

    </div>
  </b-modal>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  name: 'BillingAddTpaModal',
  components: { DatePicker },
  props: {
    value: { type: Boolean, default: false },
    underwriterName: { type: String, default: '' },
    availableTpas: { type: Array, default: function() { return [] } },
    // False when Lincoln could not be asked which TPAs share a lead key, so the
    // list is every unconfigured TPA (LIQ-2221 AC5).
    optionsScoped: { type: Boolean, default: true },
  },
  data() {
    return {
      visible: this.value,
      step: 1,
      tpaSearch: '',
      selectedIds: [],
      billingStartPeriod: null,
      saving: false,
      errorMsg: null,
    }
  },
  watch: {
    value(val) {
      this.visible = val
    },
    visible(val) {
      this.$emit('input', val)
      if (!val) this.reset()
    },
  },
  computed: {
    filteredTpas() {
      var q = this.tpaSearch ? this.tpaSearch.toLowerCase().trim() : ''
      return this.availableTpas.filter(function(t) {
        return !q || t.name.toLowerCase().includes(q)
      })
    },
  },
  methods: {
    isSelected(id) {
      return this.selectedIds.includes(id)
    },
    toggleTpa(id) {
      var idx = this.selectedIds.indexOf(id)
      if (idx === -1) {
        this.selectedIds.push(id)
      } else {
        this.selectedIds.splice(idx, 1)
      }
    },
    getTpaName(id) {
      var tpa = this.availableTpas.find(function(t) { return t.id === id })
      return tpa ? tpa.name : id
    },
    disabledBeforeToday(date) {
      var now = new Date()
      var today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      return date < today
    },
    async confirmAdd() {
      this.errorMsg = null
      this.saving = true
      try {
        await this.$emit('add', {
          selectedIds: this.selectedIds.slice(),
          billingStartPeriod: this.billingStartPeriod || null,
          enable: !!this.billingStartPeriod,
        })
      } catch (e) {
        this.errorMsg = 'Failed to add TPA billing. Please try again.'
      } finally {
        this.saving = false
      }
    },
    reset() {
      this.step = 1
      this.tpaSearch = ''
      this.selectedIds = []
      this.billingStartPeriod = null
      this.saving = false
      this.errorMsg = null
    },
    onHide() {
      this.visible = false
    },
  },
}
</script>

<style scoped>
.atm-root { position: relative; padding: 4px 0 0; }

.atm-close {
  position: absolute; top: -8px; right: -8px;
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: transparent; color: #8898aa; font-size: 1.4rem;
  cursor: pointer; padding: 0; line-height: 1;
  transition: background 0.15s, color 0.15s;
}
.atm-close:hover { background: #f0f0f0; color: #32325d; }

.atm-header {
  display: flex; align-items: flex-start; gap: 14px; margin-bottom: 20px;
}
.atm-header__icon-wrap {
  display: flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 50%;
  background: #e8f4f0; flex-shrink: 0;
}
.atm-header__icon { font-size: 1.4rem; color: #1c4a50; }
.atm-header__title { font-size: 1rem; font-weight: 700; color: #32325d; margin: 0 0 4px; }
.atm-header__sub { font-size: 0.88rem; color: #525f7f; margin: 0; }

.atm-section-label {
  font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.05em; color: #8898aa; margin-bottom: 10px;
}

.atm-search { max-width: 100%; }

.atm-tpa-list {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  margin-bottom: 16px;
}

.atm-tpa-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.1s;
}
.atm-tpa-item:last-child { border-bottom: none; }
.atm-tpa-item:hover { background: #f8f9fa; }
.atm-tpa-item--selected { background: #eaf6ff; }
.atm-tpa-item--selected:hover { background: #d9eef9; }
.atm-tpa-item__check { color: #1c4a50; font-size: 15px; flex-shrink: 0; }
.atm-tpa-item__name { color: #32325d; }

.atm-selected-summary { display: flex; align-items: center; }
.gap-2 { gap: 0.5rem; }

.atm-chips {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.atm-chip {
  background: #e8f4f0;
  color: #1c4a50;
  border: 1px solid #b8d8d2;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 500;
}

.atm-date-wrap { margin-bottom: 8px; }
.atm-date-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #525f7f;
  margin-bottom: 6px;
}
.atm-date-picker { width: 200px; }
.atm-date-picker >>> .mx-input {
  height: 34px;
  font-size: 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  box-shadow: none;
  color: #495057;
}
.atm-date-picker >>> .mx-input:focus {
  border-color: #1c4a50;
  box-shadow: 0 0 0 2px rgba(28,74,80,0.12);
}
.atm-date-picker >>> .mx-table-date .cell.active {
  background: #1c4a50 !important;
  border-radius: 4px;
}
.atm-date-hint { font-size: 11px; color: #adb5bd; margin-top: 6px; margin-bottom: 0; }

.atm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid #f1f3f5;
  margin-top: 8px;
}
</style>